const money = (n) => `$${Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n) => `${Number(n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

export { money };

function Line({ label, value, testid, muted, strong, note }) {
  return (
    <div className={`flex justify-between gap-4 ${strong ? "pt-2 mt-1 border-t border-[#E7E5E4]" : ""}`}>
      <span className={muted ? "text-[#78716C]" : strong ? "text-[#1C1917] font-medium" : "text-[#44403C]"}>
        {label}{note && <span className="block text-xs text-[#78716C]">{note}</span>}
      </span>
      <span data-testid={testid} className={`shrink-0 ${strong ? "text-[#1C1917] font-medium" : "text-[#1C1917]"}`}>{value}</span>
    </div>
  );
}

function stayLabel(bd) {
  if (bd.pricing_basis !== "monthly") return `${bd.nights} nights`;
  const parts = [];
  if (bd.full_months) parts.push(`${bd.full_months} month${bd.full_months === 1 ? "" : "s"}`);
  if (bd.extra_nights) parts.push(`${bd.extra_nights} night${bd.extra_nights === 1 ? "" : "s"}`);
  return `${parts.join(" + ")} (${bd.nights} nights)`;
}

/** Itemized estimate / invoice built from the backend fee engine's breakdown object. */
export function PriceBreakdown({ bd, testidPrefix = "bd", totalLabel = "Total reservation amount", showDue = true }) {
  if (!bd) return null;
  const p = testidPrefix;
  const hasDiscount = Number(bd.discount_amount) > 0;
  const pending = bd.tax_status === "pending_verification";
  const exempt = Boolean(bd.long_term_exempt);
  const taxNote = pending ? "Pending verification" : exempt ? "Exempt · verified by Masaya" : null;
  return (
    <div data-testid={`${p}-breakdown`} className="text-sm space-y-1.5">
      {pending && (
        <p data-testid={`${p}-pending-badge`} className="inline-flex rounded-full bg-[#FEF3C7] text-[#92400E] px-2.5 py-0.5 text-xs font-medium">Pending verification</p>
      )}
      {bd.pricing_basis === "monthly" && (
        <Line label="Monthly accommodation rate" value={`${money(bd.monthly_rate)} / mo`} testid={`${p}-monthly-rate`}
          note={bd.extra_nights ? `Extra nights at ${money(bd.prorated_nightly_rate)} (monthly rate ÷ 30)` : null} />
      )}
      <Line label={`Accommodation · ${stayLabel(bd)}`} value={money(bd.accommodation_subtotal_before_discount)} testid={`${p}-accommodation`} />
      {hasDiscount && (
        <Line label={`Discount${bd.discount_label ? ` (${bd.discount_label})` : ""}`} value={`−${money(bd.discount_amount)}`} testid={`${p}-discount`} />
      )}
      <Line label="Cleaning Fee" note="One-time · non-refundable" value={money(bd.cleaning_fee)} testid={`${p}-cleaning`} />
      <Line label={`Management Fee (${pct(bd.management_fee_pct)})`} note="Of discounted accommodation · non-refundable" value={money(bd.management_fee)} testid={`${p}-management`} />
      <Line label={pending || exempt ? "Texas Tax" : `Texas Tax (${pct(bd.texas_tax_pct)})`} note={taxNote} value={money(bd.texas_tax)} testid={`${p}-texas-tax`} />
      <Line label={pending || exempt ? "Occupancy Tax" : `Occupancy Tax (${pct(bd.occupancy_tax_pct)})`} note={taxNote} value={money(bd.occupancy_tax)} testid={`${p}-occupancy-tax`} />
      <Line strong label={pending ? `Estimated ${totalLabel.toLowerCase()}` : totalLabel} note={pending ? "Assumes the permanent-resident exemption · pending verification" : null} value={money(bd.total)} testid={`${p}-total`} />
      {pending && bd.taxable_total !== undefined && (
        <p data-testid={`${p}-if-not-eligible`} className="text-xs text-[#78716C] text-right">If not eligible: {money(bd.taxable_total)} incl. taxes</p>
      )}
      <Line muted label="Refundable security deposit" note={Number(bd.security_deposit) > 0 ? "Refundable · held separately" : "Not charged"} value={money(bd.security_deposit)} testid={`${p}-deposit`} />
      {showDue && bd.amount_due_today !== undefined && (
        <div className="mt-2 rounded-lg bg-[#F5F5F4] p-3 space-y-1">
          <Line label="Amount due today" value={money(bd.amount_due_today)} testid={`${p}-due-today`} />
          <Line muted label="Remaining balance" value={money(bd.remaining_balance)} testid={`${p}-remaining`} />
        </div>
      )}
      {pending && (
        <p data-testid={`${p}-pending-note`} className="pt-1 text-xs text-[#92400E]">
          Stays of 30+ consecutive nights are estimated with the permanent-resident exemption (Texas Tax and Occupancy Tax $0.00). Masaya verifies eligibility before any payment is requested; if you're not eligible, taxes are added and you'll approve the revised total first.
        </p>
      )}
      {exempt && <p className="pt-1 text-xs text-[#4A6A48]">Permanent-resident exemption verified by Masaya: Texas Tax and Occupancy Tax are final at $0.00.</p>}
      {!exempt && !pending && bd.venue_tax_notice && <p className="pt-1 text-xs text-[#78716C]">{bd.venue_tax_notice}</p>}
    </div>
  );
}

const STATUS = { paid: "Paid", partially_paid: "Partially paid", link_sent: "Payment link ready", due: "Upcoming" };
const PART_STATUS = { paid: "Paid", link_sent: "Link ready", due: "Upcoming" };

/** Monthly payment schedule. `rows` may be breakdown.schedule or ledger.schedule (with status). */
export function PaymentSchedule({ rows, testidPrefix = "schedule" }) {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length === 0) return null;
  return (
    <div data-testid={`${testidPrefix}-list`} className="text-sm divide-y divide-[#E7E5E4]">
      {list.map((s) => (
        <div key={s.installment} data-testid={`${testidPrefix}-row-${s.installment}`} className="py-2">
          <div className="flex justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[#1C1917]">{s.label} <span className="text-xs text-[#78716C]">· {s.period_start} → {s.period_end}</span></p>
              <p className="text-xs text-[#78716C]">
                {s.due_label}{s.status ? ` · ${STATUS[s.status] || s.status}` : ""}
                {" · "}Rent {money(s.rent)} + mgmt {money(s.management_fee)}{s.cleaning_fee ? ` + cleaning ${money(s.cleaning_fee)}` : ""}
                {s.texas_tax + s.occupancy_tax > 0 ? ` + taxes ${money(s.texas_tax + s.occupancy_tax)}` : ""}
              </p>
            </div>
            <span data-testid={`${testidPrefix}-amount-${s.installment}`} className={`shrink-0 ${s.status === "paid" ? "text-[#4A6A48]" : "text-[#1C1917]"}`}>{money(s.amount)}</span>
          </div>
          {Array.isArray(s.split) && (
            <div className="mt-2 ml-3 pl-3 border-l-2 border-[#E7E5E4] space-y-1" data-testid={`${testidPrefix}-split-${s.installment}`}>
              {s.split.map((p) => (
                <div key={p.part} data-testid={`${testidPrefix}-part-${s.installment}-${p.part}`} className="flex justify-between gap-3 text-xs">
                  <span className="text-[#78716C]">{p.label}{p.due_date ? ` · due ${p.due_date}` : " · due at booking"} · {PART_STATUS[p.status] || p.status}</span>
                  <span className={`shrink-0 ${p.status === "paid" ? "text-[#4A6A48]" : "text-[#1C1917]"}`}>{money(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
